import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  listarInvitacionesPendientes,
  aceptarInvitacion,
  rechazarInvitacion,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Inbox, Check, X, Building2 } from "lucide-react";
import { notifyDataChanged } from "@/lib/events";

export default function InvitacionesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listarInvitacionesPendientes();
      setItems(res.data || []);
    } catch (e) {
      toast.error("Error cargando invitaciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAccept = async (share) => {
    setBusy(share.id);
    try {
      await aceptarInvitacion(share.id);
      toast.success(`Invitación aceptada a '${share.empresa_nombre || "empresa"}'`);
      await load();
      notifyDataChanged("empresas"); // la empresa compartida debe aparecer en el sidebar
      navigate(`/empresas/${share.empresa_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aceptar");
    } finally {
      setBusy(null);
    }
  };

  const handleReject = async (share) => {
    setBusy(share.id);
    try {
      await rechazarInvitacion(share.id);
      toast.success("Invitación rechazada");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al rechazar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="page-title">Invitaciones</h1>
        <p className="text-muted-foreground mt-1">
          Empresas a las que te han invitado a colaborar
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Inbox className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">Sin invitaciones</h3>
            <p className="text-muted-foreground text-center max-w-sm">
              Cuando alguien te invite a colaborar en una empresa, aparecerá aquí.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <Card key={s.id} data-testid={`invitacion-${s.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle>{s.empresa_nombre || "Empresa"}</CardTitle>
                      <CardDescription>
                        Permiso de{" "}
                        <Badge variant={s.permission === "write" ? "default" : "outline"} className="ml-1">
                          {s.permission === "write" ? "lectura + escritura" : "solo lectura"}
                        </Badge>
                      </CardDescription>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(s.created_at).toLocaleDateString("es-ES")}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleReject(s)}
                    disabled={busy === s.id}
                    data-testid={`reject-${s.id}`}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Rechazar
                  </Button>
                  <Button
                    onClick={() => handleAccept(s)}
                    disabled={busy === s.id}
                    data-testid={`accept-${s.id}`}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Aceptar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
